import { useEffect, useState } from 'react';
import { GrowthBookProvider } from '@growthbook/growthbook-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { Provider } from 'react-redux';
import { createRoot } from 'react-dom/client';
import { Center } from '@tempmee/react';
import { Spinner } from './components/commonComponents/Spinner';
// Global stylesheet order is load-bearing and is declared here, in one place,
// rather than being an accident of which module happened to import what.
//
//   1. Bootstrap 3 — still needed by the un-migrated pages.
//   2. @tempmee/react — the design system (includes Tailwind Preflight).
//   3. @tempmee/react-blocks — carries its own @container rules; Best in Dental
//      uses its TableCard. Sits with the DS, before the Bootstrap shims.
//   4. bootstrap4-shim — the subset of BS4 rules the app still uses (card,
//      custom-select). Must come AFTER the DS so it wins over Preflight's
//      element resets (e.g. `select { padding: 0 }`). Delete individual rules
//      as pages migrate to DS components; delete the file when nothing needs it.
//   5. designSystemCompat — undoes the Bootstrap 3 declarations that break the
//      design system. Must come last. Delete it with Bootstrap 3.
import 'bootstrap/dist/css/bootstrap.min.css';
import '@tempmee/react/styles.css';
import '@tempmee/react-blocks/styles.css';
import './themes/bootstrap4-shim.css';
import './themes/designSystemCompat.css';
import { initFirebase } from './apis/firebase';
import { queryClient } from './queryClient';
import App from './components/App';
import store from './store';

import { growthbook } from './growthbook';

// Firebase Remote Config initialises here rather than at import time in
// apis/firebase.js, so importing the app tree does not require credentials.
initFirebase();

const Root = () => {
  const [isReady, setIsReady] = useState(false);

  const init = async () => {
    try {
      await growthbook.init();
    } catch (error) {
      console.error(`App Init Failed: ${error?.message}`);
    }
    setIsReady(true);
  };

  useEffect(() => {
    init();
  }, []);

  if (!isReady) {
    // The app shell does not exist yet, so this owns its own centring rather
    // than getting it from Spinner — which stays layout-free so it can also be
    // dropped inline next to a heading without moving it.
    return (
      <Center height="100vh">
        <Spinner />
      </Center>
    );
  }

  return (
    <GrowthBookProvider growthbook={growthbook}>
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </Provider>
    </GrowthBookProvider>
  );
};

// Get the root element
const container = document.querySelector('#root');

// Create a root
const root = createRoot(container);
root.render(<Root />);
